// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useGroupDetailQuery, useUpdateGroupMutation } from "@/api/hooks/use-groups";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  BO_NODE_DESCRIPTIONS,
  BO_PERMISSION_SECTIONS,
  boPermissionSetsEqual,
  MANAGED_BO_PERMISSIONS,
} from "@/features/iam/permission-trees";
import { permissionMatches } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";
import { cn } from "@/lib/utils";

export interface GroupBoPermissionsTabHandle {
  reset: () => void;
}

interface GroupBoPermissionsTabProps {
  groupName: string;
  canEdit: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<GroupBoPermissionsTabHandle>;
}

export function GroupBoPermissionsTab({ groupName, canEdit, onDirtyChange, ref }: GroupBoPermissionsTabProps) {
  const { t } = useTranslation("back-office");
  const { debugMode } = useDebugMode();
  const { data } = useGroupDetailQuery(groupName);
  const updateGroup = useUpdateGroupMutation(groupName);

  // Hide rows/sections for pages that are only reachable in debug mode.
  const sections = useMemo(
    () =>
      BO_PERMISSION_SECTIONS.filter((section) => debugMode || !section.debugOnly).map((section) => ({
        ...section,
        nodes: section.nodes.filter((node) => debugMode || !node.debugOnly),
      })),
    [debugMode],
  );

  // The group's full flat permission set.
  const allPermissions = useMemo(() => data?.permissions ?? [], [data]);
  // The slice this tab manages: the backoffice-permission domain (may include wildcards).
  const managedPermissions = useMemo(
    () => new Set(allPermissions.filter((permission) => permission.startsWith("backoffice:"))),
    [allPermissions],
  );

  // Baseline the dirty check compares against. Tracks the persisted state, advanced on a successful
  // save so the form clears immediately — without waiting for the refetched data.
  const [baseline, setBaseline] = useState<Set<string>>(() => new Set(managedPermissions));
  const [enabled, setEnabled] = useState<Set<string>>(() => new Set(managedPermissions));

  useEffect(
    function resetWhenDataLoads() {
      setEnabled(new Set(managedPermissions));
      setBaseline(new Set(managedPermissions));
    },
    [managedPermissions],
  );

  const isDirty = !boPermissionSetsEqual(enabled, baseline);

  useImperativeHandle(ref, () => ({ reset: () => setEnabled(new Set(baseline)) }));

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(
    function notifyParentOfDirtyState() {
      onDirtyChangeRef.current?.(isDirty);
    },
    [isDirty],
  );

  // A broader enabled permission that already covers `required` (e.g. a section wildcard).
  function coveredByOther(required: string): string | undefined {
    for (const granted of enabled) {
      if (granted !== required && permissionMatches(granted, required)) return granted;
    }
    return undefined;
  }

  // Edit implies View: enabling an edit grant also grants its view; revoking the view also revokes its edit.
  function toggleView(view: string, edit?: string) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(view)) {
        next.delete(view);
        if (edit) next.delete(edit);
      } else {
        next.add(view);
      }
      return next;
    });
  }
  function toggleEdit(view: string, edit: string) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(edit)) {
        next.delete(edit);
      } else {
        next.add(edit);
        next.add(view);
      }
      return next;
    });
  }

  function renderSwitch(permission: string | undefined, onToggle: () => void) {
    if (!permission) return <span className="text-muted-foreground">—</span>;

    const covered = coveredByOther(permission);
    if (covered) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center text-muted-foreground">
              <Switch checked disabled aria-readonly className="pointer-events-none opacity-60" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="font-mono text-xs">
            {t("users.permissions.grantedVia", { permission: covered })}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <Switch checked={enabled.has(permission)} disabled={!canEdit} onCheckedChange={onToggle} />;
  }

  async function onSave() {
    if (!data) return;
    // Resend the full permission set: the slices this tab doesn't manage (the product domain &
    // unknown grants, preserved as-is) plus the switches enabled here.
    const preserved = allPermissions.filter((permission) => !MANAGED_BO_PERMISSIONS.has(permission));
    const permissions = [...new Set([...preserved, ...enabled])];
    try {
      await updateGroup.mutateAsync({ permissions });
      setBaseline(new Set(enabled));
      toast.success(t("groups.notifications.updated", { name: data.group.name }));
    } catch {
      // Error toast handled by API middleware
    }
  }

  // A single grid row: label cell + View cell + Edit cell. Borders come from the grid container.
  function gridRow(
    key: string,
    label: React.ReactNode,
    view: React.ReactNode,
    edit: React.ReactNode,
    variant?: "header" | "section" | "node",
    separator?: boolean,
    description?: string,
  ) {
    const labelClass =
      variant === "header" ? "text-xs text-muted-foreground" : variant === "section" ? "font-medium" : undefined;
    return (
      <Fragment key={key}>
        <div className={cn("px-3 py-2", variant === "node" && "pl-6", separator && "border-b")}>
          {description ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-4",
                    labelClass,
                  )}
                >
                  {label}
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">{description}</TooltipContent>
            </Tooltip>
          ) : (
            <span className={labelClass}>{label}</span>
          )}
        </div>
        <div className="min-h-9 justify-center py-1.5">{view}</div>
        <div className="min-h-9 justify-center py-1.5">{edit}</div>
      </Fragment>
    );
  }

  const dash = <span className="text-muted-foreground">—</span>;

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <div
        className={cn(
          "grid grid-cols-[1fr_3.5rem_3.5rem] text-sm",
          "[&>*]:flex [&>*]:items-center",
          "[&>*:nth-child(3n+2)]:border-l [&>*:nth-child(3n+2)]:border-b",
          "[&>*:nth-child(3n+3)]:border-x [&>*:nth-child(3n+3)]:border-b",
          "[&>*:nth-child(2)]:border-t [&>*:nth-child(3)]:border-t",
        )}
      >
        {gridRow("__header", null, t("users.permissions.view"), t("users.permissions.edit"), "header")}

        {/* Full access — the top-level `backoffice:*` / `backoffice:*:edit` wildcards */}
        {gridRow(
          "__full-access",
          t("users.permissions.fullAccess"),
          renderSwitch("backoffice:*", () => toggleView("backoffice:*", "backoffice:*:edit")),
          renderSwitch("backoffice:*:edit", () => toggleEdit("backoffice:*", "backoffice:*:edit")),
          "section",
          true,
          t("users.permissions.everyBackofficePermission"),
        )}

        {sections.map((section, sectionIndex) => {
          const viewWildcard = `${section.prefix}:*`;
          const editWildcard = `${section.prefix}:*:edit`;
          const hasEdit = section.nodes.some((node) => node.edit);
          const isLastSection = sectionIndex === sections.length - 1;
          return (
            <Fragment key={section.prefix}>
              {gridRow(
                `${section.prefix}__header`,
                t(section.label),
                renderSwitch(viewWildcard, () => toggleView(viewWildcard, editWildcard)),
                hasEdit ? renderSwitch(editWildcard, () => toggleEdit(viewWildcard, editWildcard)) : dash,
                "section",
                false,
                t("users.permissions.allPermissions", { section: t(section.label) }),
              )}
              {section.nodes.map((node, nodeIndex) =>
                gridRow(
                  node.view,
                  t(node.label),
                  renderSwitch(node.view, () => toggleView(node.view, node.edit)),
                  node.edit ? renderSwitch(node.edit, () => toggleEdit(node.view, node.edit as string)) : dash,
                  "node",
                  !isLastSection && nodeIndex === section.nodes.length - 1,
                  BO_NODE_DESCRIPTIONS[node.view],
                ),
              )}
            </Fragment>
          );
        })}
      </div>

      {canEdit && isDirty && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          <SaveDiscardPane onDiscard={() => setEnabled(new Set(baseline))} saving={updateGroup.isPending} />
        </form>
      )}
    </div>
  );
}
